import * as Note from "@tonaljs/note";
import * as Tone from "tone";
import {
  addMidiNote,
  addMidiTrack,
  createMidiDocument,
  downloadMidi,
  rememberMidiExportNoticeDismissed,
  shouldShowMidiExportNotice,
} from "../js/music/midi-export.js";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";

const STORAGE_KEY = "bardsCorner.clips.v1";
const PPQ = 960;
const CLIP_EDITOR_HANDOFF_KEY = "bardsCorner.clipEditor.handoff.v1";
const CLIP_EDITOR_RETURN_KEY = "bardsCorner.clipEditor.return.v1";
const DEFAULT_TITLE = "Untitled Clip";
const DEFAULT_STEPS_PER_BAR = 8;
const DEFAULT_BASE_OCTAVE = 2;
const DEFAULT_GRID_OCTAVES = 2;
const GRID_RESOLUTIONS = [
  { stepsPerBar: 4, label: "1/4" },
  { stepsPerBar: 8, label: "1/8" },
  { stepsPerBar: 16, label: "1/16" },
];
const MAX_BARS = 4;
const STEP_RECORD_DEBOUNCE_MS = 20;
const MIN_BPM = 40;
const MAX_BPM = 240;
const MIN_BASE_OCTAVE = 0;
const MAX_BASE_OCTAVE = 8;
const MAX_GRID_OCTAVES = 3;
const keys = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const noteColors = ["#c73528", "#c7771f", "#a3a226", "#4fad27", "#2d92a8", "#8c22b4", "#b821b8"];
const chromaticNoteColor = "#6f7680";
const majorSemitones = [0, 2, 4, 5, 7, 9, 11];
const minorSemitones = [0, 2, 3, 5, 7, 8, 10];
const chromaticNames = ["1", "b2", "2", "b3", "3", "4", "b5", "5", "b6", "6", "b7", "7"];
const STEP_RECORD_SCALE_KEYS = [
  ["Digit1", "1", 0], ["Digit2", "2", 1], ["Digit3", "3", 2], ["Digit4", "4", 3],
  ["Digit5", "5", 4], ["Digit6", "6", 5], ["Digit7", "7", 6], ["Digit8", "8", 7],
];
const STEP_RECORD_WHITE_KEYS = [
  ["KeyA", "A", 0], ["KeyS", "S", 1], ["KeyD", "D", 2], ["KeyF", "F", 3],
  ["KeyG", "G", 4], ["KeyH", "H", 5], ["KeyJ", "J", 6], ["KeyK", "K", 7],
];
const STEP_RECORD_CHROMATIC_KEYS = [
  ["KeyA", "A", 0], ["KeyW", "W", 1], ["KeyS", "S", 2], ["KeyE", "E", 3],
  ["KeyD", "D", 4], ["KeyF", "F", 5], ["KeyT", "T", 6], ["KeyG", "G", 7],
  ["KeyY", "Y", 8], ["KeyH", "H", 9], ["KeyU", "U", 10], ["KeyJ", "J", 11],
  ["KeyK", "K", 12],
];
const STEP_RECORD_BINDING_ROWS = {
  scale: [
    STEP_RECORD_SCALE_KEYS,
    STEP_RECORD_WHITE_KEYS,
  ],
  chromatic: [
    [["KeyW", "W", 1], ["KeyE", "E", 3], null, ["KeyT", "T", 6], ["KeyY", "Y", 8], ["KeyU", "U", 10]],
    [["KeyA", "A", 0], ["KeyS", "S", 2], ["KeyD", "D", 4], ["KeyF", "F", 5], ["KeyG", "G", 7], ["KeyH", "H", 9], ["KeyJ", "J", 11], ["KeyK", "K", 12]],
    STEP_RECORD_SCALE_KEYS,
  ],
};
const ADDITIONAL_FATBOY_SOUND_IDS = [
  "accordion",
  "acoustic_guitar_steel",
  "alto_sax",
  "applause",
  "bagpipe",
  "banjo",
  "baritone_sax",
  "bassoon",
  "blown_bottle",
  "brass_section",
  "bright_acoustic_piano",
  "celesta",
  "cello",
  "choir_aahs",
  "church_organ",
  "clarinet",
  "contrabass",
  "dulcimer",
  "electric_bass_finger",
  "electric_bass_pick",
  "electric_grand_piano",
  "english_horn",
  "fiddle",
  "flute",
  "french_horn",
  "fretless_bass",
  "glockenspiel",
  "harmonica",
  "honkytonk_piano",
  "kalimba",
  "koto",
  "lead_1_square",
  "lead_2_sawtooth",
  "lead_3_calliope",
  "lead_4_chiff",
  "lead_5_charang",
  "lead_6_voice",
  "lead_7_fifths",
  "lead_8_bass__lead",
  "marimba",
  "melodic_tom",
  "music_box",
  "muted_trumpet",
  "oboe",
  "ocarina",
  "orchestral_harp",
  "overdriven_guitar",
  "pad_1_new_age",
  "pad_2_warm",
  "pad_3_polysynth",
  "pad_4_choir",
  "pad_5_bowed",
  "pad_6_metallic",
  "pad_7_halo",
  "pad_8_sweep",
  "pan_flute",
  "percussive_organ",
  "piccolo",
  "recorder",
  "reverse_cymbal",
  "shakuhachi",
  "shamisen",
  "shanai",
  "sitar",
  "slap_bass_1",
  "slap_bass_2",
  "soprano_sax",
  "steel_drums",
  "string_ensemble_1",
  "string_ensemble_2",
  "synth_bass_1",
  "synth_bass_2",
  "synth_brass_1",
  "synth_brass_2",
  "synth_choir",
  "synth_drum",
  "synth_strings_1",
  "synth_strings_2",
  "tango_accordion",
  "tenor_sax",
  "trombone",
  "trumpet",
  "tuba",
  "tubular_bells",
  "viola",
  "violin",
];
const SOUND_LABEL_WORD_OVERRIDES = {
  aahs: "Aahs",
  bass: "Bass",
  sax: "Sax",
};

function soundfontLabelFromId(id) {
  return id
    .split("_")
    .filter(Boolean)
    .map((word) => SOUND_LABEL_WORD_OVERRIDES[word] || `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

const FATBOY_SOUNDFONTS = [
  { id: "acoustic_grand_piano", label: "Acoustic Grand Piano", globalName: "acoustic_grand_piano", gain: 0.95, defaultVelocity: 0.65, release: 1.2 },
  { id: "electric_piano_1", label: "Electric Piano 1", globalName: "electric_piano_1", gain: 0.95, defaultVelocity: 0.68, release: 1.6 },
  { id: "electric_guitar_clean", label: "Electric Guitar Clean", globalName: "electric_guitar_clean", gain: 0.9, defaultVelocity: 0.72, release: 1.1 },
  { id: "acoustic_guitar_nylon", label: "Acoustic Guitar Nylon", globalName: "acoustic_guitar_nylon", gain: 0.95, defaultVelocity: 0.72, release: 1.2 },
  { id: "acoustic_bass", label: "Acoustic Bass", globalName: "acoustic_bass", gain: 0.95, defaultVelocity: 0.72, release: 1.0 },
  { id: "clavinet", label: "Clavinet", globalName: "clavinet", gain: 0.9, defaultVelocity: 0.72, release: 0.7 },
  { id: "distortion_guitar", label: "Distortion Guitar", globalName: "distortion_guitar", gain: 0.82, defaultVelocity: 0.66, release: 1.2 },
  { id: "drawbar_organ", label: "Drawbar Organ", globalName: "drawbar_organ", gain: 0.75, defaultVelocity: 0.62, release: 0.8 },
  { id: "electric_guitar_jazz", label: "Electric Guitar Jazz", globalName: "electric_guitar_jazz", gain: 0.9, defaultVelocity: 0.7, release: 1.1 },
  { id: "electric_guitar_muted", label: "Electric Guitar Muted", globalName: "electric_guitar_muted", gain: 0.95, defaultVelocity: 0.72, release: 0.6 },
  { id: "electric_piano_2", label: "Electric Piano 2", globalName: "electric_piano_2", gain: 0.95, defaultVelocity: 0.68, release: 1.6 },
  { id: "harpsichord", label: "Harpsichord", globalName: "harpsichord", gain: 0.85, defaultVelocity: 0.72, release: 0.7 },
  { id: "pizzicato_strings", label: "Pizzicato Strings", globalName: "pizzicato_strings", gain: 0.9, defaultVelocity: 0.7, release: 0.8 },
  { id: "reed_organ", label: "Reed Organ", globalName: "reed_organ", gain: 0.78, defaultVelocity: 0.62, release: 0.8 },
  { id: "rock_organ", label: "Rock Organ", globalName: "rock_organ", gain: 0.78, defaultVelocity: 0.62, release: 0.8 },
  { id: "vibraphone", label: "Vibraphone", globalName: "vibraphone", gain: 0.9, defaultVelocity: 0.68, release: 1.8 },
  { id: "woodblock", label: "Woodblock", globalName: "woodblock", gain: 0.9, defaultVelocity: 0.72, release: 0.4 },
  { id: "xylophone", label: "Xylophone", globalName: "xylophone", gain: 0.9, defaultVelocity: 0.7, release: 0.7 },
  ...ADDITIONAL_FATBOY_SOUND_IDS.map((id) => ({
    id,
    label: soundfontLabelFromId(id),
    globalName: id,
    gain: 0.9,
    defaultVelocity: 0.68,
    release: 1.2,
  })),
  { id: "drums", label: "Drums", globalName: "drums", gain: 0.9, defaultVelocity: 0.8, release: 0.2 },
];
const LEGACY_SOUNDFONT_ALIASES = {
  piano: "acoustic_grand_piano",
  "electric piano": "electric_piano_1",
  "nylon guitar": "acoustic_guitar_nylon",
  "steel guitar": "electric_guitar_clean",
  "acoustic bass": "acoustic_bass",
  violin: "pizzicato_strings",
  strings: "pizzicato_strings",
  choir: "reed_organ",
  flute: "vibraphone",
  "square lead": "xylophone",
  "warm pad": "drawbar_organ",
  voice: "reed_organ",
};
const soundfontScriptPromises = new Map();

const els = {
  fileNewButton: document.getElementById("fileNewButton"),
  fileLoadButton: document.getElementById("fileLoadButton"),
  fileSaveButton: document.getElementById("fileSaveButton"),
  fileSaveAsButton: document.getElementById("fileSaveAsButton"),
  fileShareButton: document.getElementById("fileShareButton"),
  fileExportMidiButton: document.getElementById("fileExportMidiButton"),
  unsavedChangesIndicator: document.getElementById("unsavedChangesIndicator"),
  clipTitleInput: document.getElementById("clipTitleInput"),
  originBardIndicator: document.getElementById("originBardIndicator"),
  clipStats: document.getElementById("clipStats"),
  playButton: document.getElementById("playButton"),
  stepRecordButton: document.getElementById("stepRecordButton"),
  doubleButton: document.getElementById("doubleButton"),
  undoubleButton: document.getElementById("undoubleButton"),
  backToProgressionButton: document.getElementById("backToProgressionButton"),
  clipSettingsButton: document.getElementById("clipSettingsButton"),
  bpmInput: document.getElementById("bpmInput"),
  soundBankSelect: document.getElementById("soundBankSelect"),
  keySelect: document.getElementById("keySelect"),
  qualitySelect: document.getElementById("qualitySelect"),
  laneModeDropdownButton: document.getElementById("laneModeDropdownButton"),
  scaleModeButton: document.getElementById("scaleModeButton"),
  chromaticModeButton: document.getElementById("chromaticModeButton"),
  saveClipModal: document.getElementById("saveClipModal"),
  saveClipNameInput: document.getElementById("saveClipNameInput"),
  saveClipDuplicateMessage: document.getElementById("saveClipDuplicateMessage"),
  saveClipTypeSelect: document.getElementById("saveClipTypeSelect"),
  saveSharedCheckbox: document.getElementById("saveSharedCheckbox"),
  saveSharedInfo: document.getElementById("saveSharedInfo"),
  saveStockCheckbox: document.getElementById("saveStockCheckbox"),
  saveStockCheckboxWrap: document.getElementById("saveStockCheckboxWrap"),
  saveExistingClipList: document.getElementById("saveExistingClipList"),
  saveClipMessage: document.getElementById("saveClipMessage"),
  confirmSaveClipButton: document.getElementById("confirmSaveClipButton"),
  shareModal: document.getElementById("shareModal"),
  shareLinkHelp: document.getElementById("shareLinkHelp"),
  shareLinkInput: document.getElementById("shareLinkInput"),
  shareLinkMessage: document.getElementById("shareLinkMessage"),
  copyShareLinkButton: document.getElementById("copyShareLinkButton"),
  midiExportModal: document.getElementById("midiExportModal"),
  confirmMidiExportButton: document.getElementById("confirmMidiExportButton"),
  midiExportNeverShowButton: document.getElementById("midiExportNeverShowButton"),
  clipGrid: document.getElementById("clipGrid"),
  clipGridFooter: document.getElementById("clipGridFooter"),
  keyboardBindings: document.getElementById("keyboardBindings"),
  clipSettingsModal: document.getElementById("clipSettingsModal"),
  gridOctaves1Radio: document.getElementById("gridOctaves1Radio"),
  gridOctaves2Radio: document.getElementById("gridOctaves2Radio"),
  gridOctaves3Radio: document.getElementById("gridOctaves3Radio"),
  gridResolution4Radio: document.getElementById("gridResolution4Radio"),
  gridResolution8Radio: document.getElementById("gridResolution8Radio"),
  gridResolution16Radio: document.getElementById("gridResolution16Radio"),
  octaveDownButton: document.getElementById("octaveDownButton"),
  octaveUpButton: document.getElementById("octaveUpButton"),
  baseOctaveOutput: document.getElementById("baseOctaveOutput"),
  loadClipModal: document.getElementById("loadClipModal"),
  loadClipMessage: document.getElementById("loadClipMessage"),
  loadClipList: document.getElementById("loadClipList"),
  loadClipOwnedTab: document.getElementById("loadClipOwnedTab"),
  loadClipSharedTab: document.getElementById("loadClipSharedTab"),
  loadClipOwnerSortButton: document.getElementById("loadClipOwnerSortButton"),
  confirmLoadClipButton: document.getElementById("confirmLoadClipButton"),
};

const state = {
  clip: createDefaultClip(),
  saveTimer: null,
  serverClipId: null,
  savingClip: false,
  loadingClips: false,
  loadingSaveClips: false,
  loadModalClips: [],
  loadModalMode: "owned",
  loadModalSortKey: "updatedAt",
  loadModalSortDirection: "desc",
  loadModalSelectedClipId: null,
  loadPreviewTimeoutIds: [],
  loadPreviewRequestId: null,
  saveModalClips: [],
  saveModalSelectedClipId: null,
  saveModalDuplicateClipId: null,
  saveModalSortKey: "updatedAt",
  saveModalSortDirection: "desc",
  serverClipSignature: null,
  serverClipLocked: false,
  loadedOriginBardName: "",
  loadedClipStats: null,
  currentUser: null,
  returnHandoff: null,
  playing: false,
  playbackStartedAt: 0,
  playheadAnimationFrame: null,
  samplers: new Map(),
  scheduledIds: [],
  resizingNote: null,
  stepRecord: {
    active: false,
    step: 0,
    octave: 0,
    noteLengthSteps: 1,
    pendingSemitones: new Set(),
    debounceTimer: null,
    history: [],
  },
};

function makeId(prefix) {
  if (window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function createDefaultClip() {
  const now = new Date().toISOString();

  return {
    schema: "ClipJSON",
    version: 1,
    id: makeId("clip"),
    title: DEFAULT_TITLE,
    bars: 1,
    stepsPerBar: DEFAULT_STEPS_PER_BAR,
    bpm: 120,
    soundBank: "acoustic_grand_piano",
    key: "C",
    baseOctave: DEFAULT_BASE_OCTAVE,
    gridOctaves: DEFAULT_GRID_OCTAVES,
    quality: "major",
    showChromatic: false,
    shared: false,
    stock: false,
    clipType: "melody",
    notes: [],
    createdAt: now,
    updatedAt: now,
  };
}

function clampNumber(value, min, max, fallback) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return fallback;
  }

  return Math.min(max, Math.max(min, number));
}

function normalizeClip(value) {
  const source = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
  const bars = [1, 2, 4].includes(Number(source.bars)) ? Number(source.bars) : 1;
  const stepsPerBar = validStepsPerBar(source.stepsPerBar ?? source.steps_per_bar ?? source.gridResolution ?? source.grid_resolution);
  const quality = source.quality === "minor" ? "minor" : "major";
  const key = keys.includes(source.key) ? source.key : "C";
  const baseOctave = Math.trunc(clampNumber(source.baseOctave ?? source.base_octave, MIN_BASE_OCTAVE, MAX_BASE_OCTAVE, DEFAULT_BASE_OCTAVE));
  const gridOctaves = [1, 2, 3].includes(Number(source.gridOctaves ?? source.grid_octaves))
    ? Math.trunc(Number(source.gridOctaves ?? source.grid_octaves))
    : DEFAULT_GRID_OCTAVES;
  const soundBank = normalizeSoundfontId(source.soundBank || source.sound_bank || source.instrumentName);
  const now = new Date().toISOString();

  return {
    schema: "ClipJSON",
    version: 1,
    id: String(source.id || makeId("clip")),
    title: String(source.title || DEFAULT_TITLE).slice(0, 160),
    bars,
    stepsPerBar,
    bpm: clampNumber(source.bpm, MIN_BPM, MAX_BPM, 120),
    soundBank,
    key,
    baseOctave,
    gridOctaves,
    quality,
    showChromatic: Boolean(source.showChromatic),
    shared: Boolean(source.shared),
    stock: Boolean(source.stock),
    clipType: source.clipType === "bass" || source.clipType === "drum" ? source.clipType : "melody",
    notes: normalizeNotes(source.notes, bars, quality, stepsPerBar),
    createdAt: source.createdAt || now,
    updatedAt: source.updatedAt || now,
  };
}

function validStepsPerBar(value, fallback = DEFAULT_STEPS_PER_BAR) {
  const stepsPerBar = Math.trunc(Number(value));
  return GRID_RESOLUTIONS.some((resolution) => resolution.stepsPerBar === stepsPerBar)
    ? stepsPerBar
    : fallback;
}

function normalizeSoundfontId(value, fallback = "acoustic_grand_piano") {
  const raw = String(value || "").trim();
  if (!raw) {
    return fallback;
  }
  if (FATBOY_SOUNDFONTS.some((config) => config.id === raw)) {
    return raw;
  }
  return LEGACY_SOUNDFONT_ALIASES[raw] || fallback;
}

function soundfontConfig(id) {
  return FATBOY_SOUNDFONTS.find((config) => config.id === id) || FATBOY_SOUNDFONTS[0];
}

function labelForSoundBank(value) {
  return soundfontConfig(normalizeSoundfontId(value)).label;
}

function soundfontScriptSrc(config) {
  if (config.id === "drums") {
    return "/sounds/drums/drums-mp3.js";
  }
  return `/sounds/fatboy/${config.globalName}-ogg.js`;
}

async function ensureSoundfontData(config) {
  if (window.MIDI?.Soundfont?.[config.globalName]) {
    return;
  }

  const src = soundfontScriptSrc(config);
  const existing = soundfontScriptPromises.get(src);
  if (existing) {
    await existing;
    return;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (window.MIDI?.Soundfont?.[config.globalName]) {
        resolve();
        return;
      }
      reject(new Error(`Sound bank loaded without data: ${config.label}`));
    };
    script.onerror = () => reject(new Error(`Could not load sound bank: ${config.label}`));
    document.head.append(script);
  });
  soundfontScriptPromises.set(src, loadPromise);
  await loadPromise;
}

function normalizeNotes(notes, bars, quality, stepsPerBar = state.clip.stepsPerBar || DEFAULT_STEPS_PER_BAR) {
  if (!Array.isArray(notes)) {
    return [];
  }

  const totalSteps = bars * stepsPerBar;
  const scaleSemitones = semitonesForQuality(quality);
  const seen = new Set();

  return notes.reduce((normalized, note) => {
    const step = Math.trunc(Number(note?.step));
    const semitone = Math.trunc(Number(note?.semitone));
    const durationSteps = Math.trunc(clampNumber(note?.durationSteps ?? note?.duration_steps, 1, totalSteps, 1));

    if (
      !Number.isInteger(step)
      || step < 0
      || step >= totalSteps
      || !Number.isInteger(semitone)
      || semitone < 0
      || semitone >= MAX_GRID_OCTAVES * 12
    ) {
      return normalized;
    }

    const duration = Math.min(durationSteps, totalSteps - step);
    const collides = Array.from({ length: duration }, (_unused, offset) => `${step + offset}:${semitone}`)
      .some((key) => seen.has(key));

    if (collides) {
      return normalized;
    }

    for (let offset = 0; offset < duration; offset += 1) {
      seen.add(`${step + offset}:${semitone}`);
    }

    normalized.push({
      id: String(note.id || makeId("note")),
      step,
      semitone,
      degree: scaleSemitones.indexOf(semitone % 12),
      durationSteps: duration,
    });

    return normalized;
  }, []).sort((a, b) => a.step - b.step || a.semitone - b.semitone);
}

function semitonesForQuality(quality = state.clip.quality) {
  return quality === "minor" ? minorSemitones : majorSemitones;
}

function stepRecordScaleSemitones() {
  return [...semitonesForQuality(), 12];
}

function readStoredState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readClipEditorHandoff() {
  try {
    const raw = window.localStorage.getItem(CLIP_EDITOR_HANDOFF_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadStoredState() {
  const handoff = readClipEditorHandoff();
  if (handoff?.clip && handoff?.eventId) {
    state.clip = normalizeClip(handoff.clip);
    state.serverClipId = null;
    state.serverClipSignature = null;
    state.serverClipLocked = false;
    state.loadedOriginBardName = "";
    state.loadedClipStats = null;
    state.returnHandoff = {
      returnUrl: handoff.returnUrl || "/songs",
      trackId: String(handoff.trackId || ""),
      eventId: String(handoff.eventId),
    };
    try {
      window.localStorage.removeItem(CLIP_EDITOR_HANDOFF_KEY);
    } catch {
      // Handoff cleanup is best-effort.
    }
    saveState();
    return;
  }

  const stored = readStoredState();

  if (!stored) {
    return;
  }

  state.clip = normalizeClip(stored.clip);
  state.serverClipId = stored.settings?.serverClipId ? String(stored.settings.serverClipId) : null;
  state.serverClipSignature = stored.settings?.serverClipSignature
    ? String(stored.settings.serverClipSignature)
    : null;
  state.serverClipLocked = Boolean(stored.settings?.serverClipLocked);
  state.loadedClipStats = normalizeLoadedClipStats(stored.settings?.loadedClipStats);
  state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
  state.returnHandoff = stored.settings?.returnHandoff?.eventId
    ? {
      returnUrl: stored.settings.returnHandoff.returnUrl || "/songs",
      trackId: String(stored.settings.returnHandoff.trackId || ""),
      eventId: String(stored.settings.returnHandoff.eventId),
    }
    : null;
}

function scheduleStateSave() {
  if (state.saveTimer) {
    window.clearTimeout(state.saveTimer);
  }

  state.saveTimer = window.setTimeout(saveState, 150);
}

function saveState() {
  if (state.saveTimer) {
    window.clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      clip: state.clip,
      settings: {
        serverClipId: state.serverClipId,
        serverClipSignature: state.serverClipSignature,
        serverClipLocked: state.serverClipLocked,
        loadedClipStats: state.loadedClipStats,
        returnHandoff: state.returnHandoff,
      },
    }));
  } catch {
    // Storage can be unavailable or full; the editor should keep working.
  }
}

function touchClip() {
  state.clip.updatedAt = new Date().toISOString();
  scheduleStateSave();
  renderFileMenuState();
}

function serverComparableClip(clip) {
  return {
    title: clip.title,
    bars: clip.bars,
    stepsPerBar: clip.stepsPerBar,
    bpm: clip.bpm,
    soundBank: normalizeSoundfontId(clip.soundBank),
    key: clip.key,
    baseOctave: clip.baseOctave,
    gridOctaves: clip.gridOctaves,
    quality: clip.quality,
    showChromatic: Boolean(clip.showChromatic),
    shared: Boolean(clip.shared),
    stock: Boolean(clip.stock),
    clipType: clip.clipType,
    notes: clip.notes,
  };
}

function clipServerSignature(clip = state.clip) {
  return JSON.stringify(serverComparableClip(clip));
}

function isSavedServerClipDirty() {
  return Boolean(state.serverClipId && state.serverClipSignature && clipServerSignature() !== state.serverClipSignature);
}

function isUnsavedNewClipModified() {
  const clip = state.clip;

  return (
    normalizeComparableTitle(clip.title) !== normalizeComparableTitle(DEFAULT_TITLE)
    || clip.notes.length > 0
    || clip.bars !== 1
    || clip.stepsPerBar !== DEFAULT_STEPS_PER_BAR
    || clip.bpm !== 120
    || normalizeSoundfontId(clip.soundBank) !== "acoustic_grand_piano"
    || clip.key !== "C"
    || clip.baseOctave !== DEFAULT_BASE_OCTAVE
    || clip.gridOctaves !== DEFAULT_GRID_OCTAVES
    || clip.quality !== "major"
    || clip.showChromatic
    || clip.shared
    || clip.stock
    || clip.clipType !== "melody"
  );
}

function hasUnsavedClipChanges() {
  return state.serverClipId ? isSavedServerClipDirty() : isUnsavedNewClipModified();
}

function normalizeLoadedClipStats(clip) {
  if (!clip || typeof clip !== "object") {
    return null;
  }

  const id = clip.id ? String(clip.id) : "";
  if (!id) {
    return null;
  }

  return {
    id,
    shared: Boolean(clip.shared),
    stock: Boolean(clip.stock),
    usedCount: Math.max(0, Math.trunc(Number(clip.usedCount) || 0)),
    bardName: String(clip.bardName || "").trim(),
  };
}

function confirmDiscardUnsavedClip(action) {
  if (!hasUnsavedClipChanges()) {
    return true;
  }

  return window.confirm(`${action}? Unsaved clip changes will be lost.`);
}

async function apiRequest(path, options = {}) {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(body.error || "The server could not complete that request.");
    error.status = response.status;
    throw error;
  }

  return body;
}

async function loadCurrentUser() {
  try {
    const body = await apiRequest("/api/auth/me");
    state.currentUser = body.user || null;
  } catch {
    state.currentUser = null;
  }

  renderAdminControls();
}

function renderAdminControls() {
  const isAdmin = Boolean(state.currentUser?.isAdmin);
  els.saveStockCheckboxWrap.classList.toggle("d-none", !isAdmin);
  els.saveStockCheckbox.disabled = !isAdmin;
  if (!isAdmin) {
    els.saveStockCheckbox.checked = false;
  }
  renderSaveModalState();
}

async function showSaveClipModal() {
  if (!canSaveClip()) {
    return;
  }

  const modal = window.bootstrap.Modal.getOrCreateInstance(els.saveClipModal);
  state.saveModalSelectedClipId = state.serverClipLocked ? null : state.serverClipId;
  const currentTitle = state.clip.title || DEFAULT_TITLE;
  els.saveClipNameInput.value = state.serverClipLocked ? `${currentTitle} Copy`.slice(0, 160) : currentTitle;
  els.saveClipTypeSelect.value = state.clip.clipType === "bass" ? "bass" : "melody";
  els.saveSharedCheckbox.checked = Boolean(state.clip.shared);
  renderSaveSharedInfo();
  els.saveStockCheckbox.checked = Boolean(state.currentUser?.isAdmin && state.clip.stock);
  state.loadingSaveClips = true;
  renderSaveClipList([], "Loading saved clips...");
  renderSaveModalDuplicateState();
  renderSaveModalState();
  modal.show();

  try {
    const body = await apiRequest("/api/clips?mode=my&includeClip=1");
    state.saveModalClips = (body.clips || []).filter(isClipEditorClip);
    renderSaveClipList(state.saveModalClips);
    renderSaveModalDuplicateState();
  } catch (error) {
    state.saveModalClips = [];
    renderSaveClipList([], error.message || "Could not load saved clips.");
  } finally {
    state.loadingSaveClips = false;
    renderSaveModalState();
  }
}

async function saveClip() {
  if (!canSaveClip()) {
    return;
  }

  if (!state.serverClipId) {
    await showSaveClipModal();
    return;
  }

  if (state.serverClipLocked) {
    await showSaveClipModal();
    return;
  }

  await saveClipToServer({
    title: state.clip.title,
    shared: state.clip.shared,
    stock: state.clip.stock,
    clipType: state.clip.clipType,
    serverClipId: state.serverClipId,
  });
}

async function saveClipFromModal() {
  if (!canSaveClip()) {
    return;
  }

  const duplicateClip = currentSaveDuplicateClip();
  let serverClipId = state.saveModalSelectedClipId;

  if (duplicateClip) {
    const overwrite = window.confirm(`Are you sure you want to save over "${duplicateClip.title || DEFAULT_TITLE}"?`);
    if (!overwrite) {
      return;
    }
    serverClipId = duplicateClip.id;
  }

  await saveClipToServer({
    title: els.saveClipNameInput.value,
    shared: els.saveSharedCheckbox.checked,
    stock: els.saveStockCheckbox.checked,
    clipType: els.saveClipTypeSelect.value,
    serverClipId,
  });
}

async function saveClipToServer(options = {}) {
  if (state.savingClip) {
    return;
  }
  if (!canSaveClip()) {
    return;
  }

  state.savingClip = true;
  renderFileMenuState();

  try {
    const selectedClip = options.serverClipId
      ? state.saveModalClips.find((clip) => clip.id === options.serverClipId)
      : null;
    if (selectedClip?.clipId) {
      state.clip.id = selectedClip.clipId;
    } else if (!options.serverClipId && state.serverClipId) {
      state.clip.id = makeId("clip");
    }
    state.clip.title = String(options.title ?? state.clip.title).trim().slice(0, 160) || DEFAULT_TITLE;
    state.clip.soundBank = normalizeSoundfontId(els.soundBankSelect.value);
    state.clip.shared = Boolean(options.shared ?? state.clip.shared);
    state.clip.stock = Boolean(options.stock ?? state.clip.stock);
    state.clip.clipType = options.clipType === "bass" ? "bass" : "melody";
    const body = await apiRequest("/api/clips", {
      method: "POST",
      body: JSON.stringify({
        clipId: state.clip.id,
        title: state.clip.title,
        shared: state.clip.shared,
        stock: state.clip.stock,
        clipType: state.clip.clipType,
        clipDuration: state.clip.bars,
        baseOctave: state.clip.baseOctave,
        gridOctaves: state.clip.gridOctaves,
        soundBank: state.clip.soundBank,
        clip: state.clip,
      }),
    });

    state.serverClipId = body.clip?.id ? String(body.clip.id) : state.serverClipId;
    state.serverClipLocked = Boolean(body.clip?.locked);
    state.loadedClipStats = normalizeLoadedClipStats(body.clip);
    state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
    state.serverClipSignature = clipServerSignature();
    scheduleStateSave();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.saveClipModal).hide();
  } catch (error) {
    window.alert(error.message || "Save failed.");
  } finally {
    state.savingClip = false;
    renderFileMenuState();
  }
}

function renderSaveClipList(clips, message = "") {
  const filteredClips = sortClipsForList(
    clips.filter(isClipEditorClip),
    state.saveModalSortKey,
    state.saveModalSortDirection,
  );
  els.saveClipMessage.textContent = message || (filteredClips.length ? "" : "No saved Melody or Bass clips yet.");
  els.saveExistingClipList.replaceChildren(...filteredClips.map((clip) => {
    const button = document.createElement("button");
    const active = clip.id === state.saveModalSelectedClipId;
    button.className = `list-group-item list-group-item-action bg-dark text-light border-secondary clip-list-row has-preview${active ? " active" : ""}`;
    button.type = "button";
    button.dataset.clipId = clip.id;
    button.innerHTML = `
      <span class="clip-list-preview-cell">${renderClipMiniPreview(clip)}</span>
      <span class="clip-list-title-wrap">
        <strong class="clip-list-cell clip-list-title-cell">${escapeHtml(clip.title || DEFAULT_TITLE)}</strong>
        ${renderClipListStats(clip)}
      </span>
      <span class="clip-list-cell">${escapeHtml(formatClipType(clip.clipType))}</span>
      <span class="clip-list-cell text-secondary">${escapeHtml(formatClipDate(clip.updatedAt))}</span>
      <span class="clip-list-cell text-secondary">${renderClipAccess(clip)}</span>
    `;
    button.addEventListener("click", () => selectSaveModalClip(clip.id));
    return button;
  }));
  renderSaveSortHeaders();
}

function renderSaveSortHeaders() {
  document.querySelectorAll("[data-save-sort]").forEach((button) => {
    const active = button.dataset.saveSort === state.saveModalSortKey;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-sort", active ? (state.saveModalSortDirection === "asc" ? "ascending" : "descending") : "none");
  });
}

function selectSaveModalClip(clipId) {
  const clip = state.saveModalClips.find((candidate) => candidate.id === clipId);
  if (!clip) {
    return;
  }

  state.saveModalSelectedClipId = clip.id;
  els.saveClipNameInput.value = clip.title || DEFAULT_TITLE;
  els.saveClipTypeSelect.value = clip.clipType === "bass" ? "bass" : "melody";
  els.saveSharedCheckbox.checked = Boolean(clip.shared);
  renderSaveSharedInfo();
  els.saveStockCheckbox.checked = Boolean(state.currentUser?.isAdmin && clip.stock);
  renderSaveClipList(state.saveModalClips);
  renderSaveModalDuplicateState();
}

function clearSaveModalSelectionIfEdited() {
  const selectedClip = state.saveModalClips.find((clip) => clip.id === state.saveModalSelectedClipId);
  if (!selectedClip) {
    return;
  }

  const selectedTitle = normalizeComparableTitle(selectedClip.title || DEFAULT_TITLE);
  const currentTitle = normalizeComparableTitle(els.saveClipNameInput.value || DEFAULT_TITLE);
  const selectedType = selectedClip.clipType === "bass" ? "bass" : "melody";
  const currentType = els.saveClipTypeSelect.value === "bass" ? "bass" : "melody";

  if (selectedTitle !== currentTitle || selectedType !== currentType) {
    state.saveModalSelectedClipId = null;
    renderSaveClipList(state.saveModalClips);
  }
}

function renderSaveModalDuplicateState() {
  const duplicateClip = currentSaveDuplicateClip();
  state.saveModalDuplicateClipId = duplicateClip?.id || null;
  els.saveClipNameInput.classList.toggle("is-duplicate", Boolean(duplicateClip));
  els.saveClipDuplicateMessage.textContent = duplicateClip
    ? `A ${formatClipType(duplicateClip.clipType)} clip with this name already exists.`
    : "";
}

function renderSaveSharedInfo() {
  els.saveSharedInfo.classList.toggle("d-none", !els.saveSharedCheckbox.checked);
}

function currentSaveDuplicateClip() {
  const title = normalizeComparableTitle(els.saveClipNameInput.value);
  const type = els.saveClipTypeSelect.value === "bass" ? "bass" : "melody";
  if (!title) {
    return null;
  }

  return state.saveModalClips.find((clip) => (
    clip.clipType === type
    && normalizeComparableTitle(clip.title) === title
  )) || null;
}

function renderSaveModalState() {
  els.confirmSaveClipButton.disabled = state.savingClip || state.loadingSaveClips || !canSaveBlankClip();
  els.confirmSaveClipButton.textContent = state.savingClip ? "Saving..." : "OK";
}

function canSaveBlankClip() {
  return state.clip.notes.length > 0 || Boolean(state.currentUser?.isAdmin);
}

function canSaveClip() {
  if (canSaveBlankClip()) {
    return true;
  }

  window.alert("Add at least one note before saving this clip. Admins can save blank clips.");
  return false;
}

function normalizeComparableTitle(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function formatClipType(value) {
  return value === "bass" ? "Bass" : "Melody";
}

function isClipEditorClip(clip) {
  return clip?.clipType === "melody" || clip?.clipType === "bass";
}

function formatClipAccess(clip) {
  return [
    clip.shared ? "Public" : "",
    clip.stock ? "Stock" : "",
  ].filter(Boolean).join(", ") || "Private";
}

function isReadOnlyClip(clip) {
  return Boolean(clip.locked) || Number(clip.usedCount) > 0 || Number(clip.thumbsUp) > 0;
}

function renderClipAccess(clip) {
  const labels = [formatClipAccess(clip)];
  if (isReadOnlyClip(clip)) {
    labels.push("Read-only");
  }
  return labels.map((label) => `<span class="access-chip${label === "Read-only" ? " is-read-only" : ""}">${escapeHtml(label)}</span>`).join("");
}

function leaderboardBardUrl(bardName) {
  const params = new URLSearchParams({ bardName: String(bardName || "") });
  return `/leaderboard/?${params}`;
}

async function showLoadClipModal() {
  if (!confirmDiscardUnsavedClip("Open another clip")) {
    return;
  }

  const modal = window.bootstrap.Modal.getOrCreateInstance(els.loadClipModal);
  state.loadingClips = true;
  state.loadModalSelectedClipId = null;
  cancelLoadClipPreview();
  renderLoadClipList([], "Loading saved clips...");
  modal.show();

  try {
    const [ownedBody, sharedBody] = await Promise.all([
      apiRequest("/api/clips?mode=my&includeClip=1"),
      apiRequest("/api/clips?mode=shared&includeClip=1"),
    ]);
    state.loadModalClips = [
      ...(ownedBody.clips || []),
      ...(sharedBody.clips || []).filter((clip) => !clip.owned),
    ].filter(isClipEditorClip);
    renderLoadClipList(state.loadModalClips);
  } catch (error) {
    state.loadModalClips = [];
    renderLoadClipList([], error.message || "Could not load clips.");
  } finally {
    state.loadingClips = false;
    renderLoadModalState();
  }
}

function renderLoadClipList(clips, message = "") {
  renderLoadModeTabs();
  const sharedMode = state.loadModalMode === "shared";
  if (sharedMode && state.loadModalSortKey === "access") {
    state.loadModalSortKey = "bardName";
  } else if (!sharedMode && state.loadModalSortKey === "bardName") {
    state.loadModalSortKey = "access";
  }
  if (els.loadClipOwnerSortButton) {
    els.loadClipOwnerSortButton.textContent = sharedMode ? "Bard" : "Access";
    els.loadClipOwnerSortButton.dataset.loadSort = sharedMode ? "bardName" : "access";
  }
  const filteredClips = clips.filter((clip) => (
    isClipEditorClip(clip)
    && (sharedMode ? !clip.owned : clip.owned)
  ));
  const sortedClips = sortClipsForList(filteredClips, state.loadModalSortKey, state.loadModalSortDirection);
  if (!sortedClips.some((clip) => clip.id === state.loadModalSelectedClipId)) {
    state.loadModalSelectedClipId = null;
  }
  els.loadClipMessage.textContent = message || (sortedClips.length ? "" : `No ${sharedMode ? "shared" : "owned"} Melody or Bass clips yet.`);
  els.loadClipList.replaceChildren(...sortedClips.map((clip) => {
    const row = document.createElement("div");
    const active = clip.id === state.loadModalSelectedClipId;
    row.className = `list-group-item list-group-item-action bg-dark text-light border-secondary clip-list-row has-preview${active ? " is-selected" : ""}`;
    row.setAttribute("role", "button");
    row.setAttribute("tabindex", "0");
    row.setAttribute("aria-pressed", active ? "true" : "false");
    row.dataset.clipId = clip.id;
    const accessLabel = formatClipAccess(clip);
    const ownerCell = sharedMode
      ? `<a class="clip-list-cell text-secondary" href="${escapeHtml(leaderboardBardUrl(clip.bardName))}">${escapeHtml(clip.bardName || "Shared")}</a>`
      : `<span class="clip-list-cell text-secondary">${escapeHtml(accessLabel)}</span>`;
    row.innerHTML = `
      <span class="clip-list-preview-cell">${renderClipMiniPreview(clip)}</span>
      <span class="clip-list-title-wrap">
        <strong class="clip-list-cell clip-list-title-cell">${escapeHtml(clip.title || DEFAULT_TITLE)}</strong>
        ${renderClipListStats(clip)}
      </span>
      <span class="clip-list-cell">${escapeHtml(formatClipType(clip.clipType))}</span>
      <span class="clip-list-cell">${escapeHtml(labelForSoundBank(clip.soundBank))}</span>
      <span class="clip-list-cell text-secondary">${escapeHtml(formatClipDate(clip.updatedAt))}</span>
      ${ownerCell}
    `;
    row.querySelector("a")?.addEventListener("click", (event) => event.stopPropagation());
    row.addEventListener("click", () => selectLoadModalClip(clip.id));
    row.addEventListener("dblclick", () => loadClipFromServer(clip.id));
    row.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        selectLoadModalClip(clip.id);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        loadSelectedClipFromModal();
      }
    });
    return row;
  }));
  renderLoadModalState();
  renderLoadSortHeaders();
}

function renderLoadModalState() {
  if (!els.confirmLoadClipButton) {
    return;
  }

  els.confirmLoadClipButton.disabled = state.loadingClips || !state.loadModalSelectedClipId;
}

function selectLoadModalClip(clipId) {
  if (state.loadingClips) {
    return;
  }

  const clip = state.loadModalClips.find((candidate) => candidate.id === clipId);
  if (!clip) {
    return;
  }

  state.loadModalSelectedClipId = clip.id;
  els.loadClipList.querySelectorAll(".clip-list-row[data-clip-id]").forEach((row) => {
    const active = row.dataset.clipId === clip.id;
    row.classList.toggle("is-selected", active);
    row.setAttribute("aria-pressed", active ? "true" : "false");
  });
  renderLoadModalState();
  previewLoadModalClip(clip);
}

function loadSelectedClipFromModal() {
  if (!state.loadModalSelectedClipId || state.loadingClips) {
    return;
  }

  loadClipFromServer(state.loadModalSelectedClipId);
}

async function previewLoadModalClip(serverClip) {
  const previewRequestId = makeId("load_preview");
  state.loadPreviewRequestId = previewRequestId;
  cancelLoadClipPreview({ keepRequest: true });

  try {
    const clip = normalizeClip(serverClip.clip || serverClip);
    await Tone.start();
    const samplerState = await ensureSamplerForSoundfont(clip.soundBank);
    if (state.loadPreviewRequestId !== previewRequestId) {
      return;
    }

    const rootMidi = Note.midi(`${clip.key}${clip.baseOctave}`) || 60;
    const stepSeconds = (60 / clip.bpm * 4) / clip.stepsPerBar;
    for (const note of clip.notes) {
      const timeoutId = window.setTimeout(() => {
        const noteName = Tone.Frequency(rootMidi + note.semitone, "midi").toNote();
        const duration = Math.max(0.08, note.durationSteps * stepSeconds * 0.92);
        samplerState?.sampler?.triggerAttackRelease(
          noteName,
          duration,
          Tone.now(),
          0.72 * (samplerState?.config.defaultVelocity || 0.7),
        );
        state.loadPreviewTimeoutIds = state.loadPreviewTimeoutIds.filter((id) => id !== timeoutId);
      }, Math.max(0, note.step * stepSeconds * 1000));
      state.loadPreviewTimeoutIds.push(timeoutId);
    }
  } catch (error) {
    if (state.loadPreviewRequestId === previewRequestId) {
      els.loadClipMessage.textContent = error.message || "Could not preview that clip.";
    }
  }
}

function cancelLoadClipPreview(options = {}) {
  for (const id of state.loadPreviewTimeoutIds) {
    window.clearTimeout(id);
  }
  state.loadPreviewTimeoutIds = [];
  if (!options.keepRequest) {
    state.loadPreviewRequestId = null;
  }
  state.samplers.forEach((samplerState) => samplerState.sampler?.releaseAll?.());
}

function renderClipListStats(clip) {
  const thumbs = Math.max(0, Math.trunc(Number(clip.thumbsUp) || 0));
  const uses = Math.max(0, Math.trunc(Number(clip.usedCount) || 0));
  const score = Math.max(0, Math.trunc(Number(clip.score) || thumbs + uses));
  return `
    <span class="clip-list-stats" aria-label="${thumbs} thumbs, ${uses} uses, score ${score}">
      <span title="Thumbs">${thumbs} thumbs</span>
      <span title="Uses">${uses} uses</span>
      <span title="Score">${score} score</span>
    </span>
  `;
}

function renderClipMiniPreview(serverClip) {
  const clip = normalizeClip(serverClip.clip || serverClip);
  const totalSteps = Math.max(1, clip.bars * clip.stepsPerBar);
  const laneCount = Math.max(1, clip.gridOctaves * 12);
  const notes = clip.notes.map((note) => {
    const left = (note.step / totalSteps) * 100;
    const width = (Math.max(1, note.durationSteps) / totalSteps) * 100;
    const top = ((laneCount - note.semitone - 1) / laneCount) * 100;
    const height = Math.max(10, 100 / laneCount);
    const degree = semitonesForQuality(clip.quality).indexOf(note.semitone % 12);
    const color = degree >= 0 ? noteColors[degree] : chromaticNoteColor;
    return `<span class="clip-list-preview-note" style="left:${left}%;width:${width}%;top:${top}%;height:${height}%;--note-color:${color};"></span>`;
  }).join("");

  return `<span class="clip-list-preview" aria-hidden="true">${notes}</span>`;
}

function renderLoadModeTabs() {
  const shared = state.loadModalMode === "shared";
  els.loadClipOwnedTab.classList.toggle("active", !shared);
  els.loadClipOwnedTab.setAttribute("aria-selected", shared ? "false" : "true");
  els.loadClipSharedTab.classList.toggle("active", shared);
  els.loadClipSharedTab.setAttribute("aria-selected", shared ? "true" : "false");
}

function sortClipsForList(clips, sortKey, sortDirection) {
  const direction = sortDirection === "asc" ? 1 : -1;
  const valueForClip = (clip) => {
    if (sortKey === "clipType") {
      return formatClipType(clip.clipType);
    }
    if (sortKey === "soundBank") {
      return labelForSoundBank(clip.soundBank);
    }
    if (sortKey === "updatedAt") {
      return new Date(clip.updatedAt).getTime() || 0;
    }
    if (sortKey === "access") {
      return formatClipAccess(clip);
    }
    if (sortKey === "bardName") {
      return clip.bardName || "Shared";
    }
    return clip.title || DEFAULT_TITLE;
  };

  return [...clips].sort((first, second) => {
    const firstValue = valueForClip(first);
    const secondValue = valueForClip(second);
    if (typeof firstValue === "number" || typeof secondValue === "number") {
      return ((firstValue || 0) - (secondValue || 0)) * direction;
    }

    const compared = String(firstValue).localeCompare(String(secondValue), undefined, { sensitivity: "base" });
    return compared * direction || String(first.id).localeCompare(String(second.id));
  });
}

function renderLoadSortHeaders() {
  document.querySelectorAll("[data-load-sort]").forEach((button) => {
    const active = button.dataset.loadSort === state.loadModalSortKey;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-sort", active ? (state.loadModalSortDirection === "asc" ? "ascending" : "descending") : "none");
  });
}

function formatClipDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

async function loadClipFromServer(clipId) {
  if (state.loadingClips) {
    return;
  }

  state.loadingClips = true;
  cancelLoadClipPreview();
  renderLoadClipList([], "Loading clip...");

  try {
    const body = await apiRequest(`/api/clips/${encodeURIComponent(clipId)}`);
    const loadedClip = body.clip?.clip;

    if (!loadedClip) {
      throw new Error("The server did not return clip JSON.");
    }

    stopPlayback();
    state.clip = normalizeClip(loadedClip);
    state.serverClipId = body.clip.id ? String(body.clip.id) : null;
    state.serverClipLocked = Boolean(body.clip.locked || !body.clip.owned);
    state.loadedClipStats = normalizeLoadedClipStats(body.clip);
    state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
    state.serverClipSignature = state.serverClipId ? clipServerSignature() : null;
    scheduleStateSave();
    render();
    window.bootstrap.Modal.getOrCreateInstance(els.loadClipModal).hide();
  } catch (error) {
    renderLoadClipList([], error.message || "Could not load that clip.");
  } finally {
    state.loadingClips = false;
  }
}

function renderFileMenuState() {
  els.fileSaveButton.disabled = state.savingClip;
  els.fileSaveButton.textContent = state.savingClip ? "Saving..." : "Save";
  els.fileSaveAsButton.disabled = state.savingClip;
  els.fileSaveAsButton.textContent = state.savingClip ? "Saving..." : "Save As";
  els.unsavedChangesIndicator.classList.toggle("d-none", !hasUnsavedClipChanges());
  renderSaveModalState();
}

function renderTitleWithOrigin() {
  els.clipTitleInput.textContent = state.clip.title || DEFAULT_TITLE;
  els.originBardIndicator.replaceChildren();
  els.originBardIndicator.classList.add("d-none");
  renderClipStats();
}

function renderClipStats() {
  const stats = state.loadedClipStats || (state.serverClipId
    ? {
      id: state.serverClipId,
      shared: Boolean(state.clip.shared),
      stock: Boolean(state.clip.stock),
      usedCount: 0,
      bardName: state.loadedOriginBardName,
    }
    : null);
  els.clipStats.replaceChildren();
  els.clipStats.classList.toggle("d-none", !stats);

  if (!stats) {
    return;
  }

  const accessPill = document.createElement("span");
  accessPill.className = "clip-stat-pill";
  accessPill.textContent = stats.shared ? "Public" : "Private";
  els.clipStats.append(accessPill);

  if (stats.stock) {
    const stockPill = document.createElement("span");
    stockPill.className = "clip-stat-pill";
    stockPill.textContent = "Stock";
    els.clipStats.append(stockPill);
  }

  const usersPill = document.createElement("span");
  usersPill.className = "clip-stat-pill";
  usersPill.textContent = `${stats.usedCount} ${stats.usedCount === 1 ? "user" : "users"}`;
  els.clipStats.append(usersPill);

  if (stats.bardName) {
    const authorPill = document.createElement("span");
    authorPill.className = "clip-stat-pill clip-stat-author";
    const byLabel = document.createElement("span");
    byLabel.textContent = "by";
    authorPill.append(byLabel);
    const link = document.createElement("a");
    link.href = leaderboardBardUrl(stats.bardName);
    link.textContent = stats.bardName;
    authorPill.append(link);
    els.clipStats.append(authorPill);
  }
}

function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function lanesForClip() {
  const scaleSemitones = semitonesForQuality();
  const totalSemitones = state.clip.gridOctaves * 12;

  if (!state.clip.showChromatic) {
    const lanes = [];

    for (let octaveOffset = 0; octaveOffset < state.clip.gridOctaves; octaveOffset += 1) {
      for (const [degree, semitone] of scaleSemitones.entries()) {
        const relativeSemitone = octaveOffset * 12 + semitone;
        lanes.push({
          semitone: relativeSemitone,
          degree,
          label: noteLabelForSemitone(relativeSemitone),
          color: noteColors[degree],
          scaleTone: true,
        });
      }
    }

    return lanes.reverse();
  }

  return Array.from({ length: totalSemitones }, (_unused, semitone) => {
    const degree = scaleSemitones.indexOf(semitone % 12);
    return {
      semitone,
      degree,
      label: noteLabelForSemitone(semitone),
      color: degree >= 0 ? noteColors[degree] : chromaticNoteColor,
      scaleTone: degree >= 0,
    };
  }).reverse();
}

function noteLabelForSemitone(relativeSemitone) {
  const rootIndex = keys.indexOf(state.clip.key);
  const absoluteIndex = rootIndex + relativeSemitone;
  const noteName = keys[((absoluteIndex % 12) + 12) % 12];
  const octave = state.clip.baseOctave + Math.floor(absoluteIndex / 12);
  return `${noteName}${octave}`;
}

function isOctaveBoundaryAbove(relativeSemitone) {
  const rootIndex = keys.indexOf(state.clip.key);
  const absoluteIndex = rootIndex + relativeSemitone;
  const nextAbsoluteIndex = absoluteIndex + 1;
  return Math.floor(nextAbsoluteIndex / 12) > Math.floor(absoluteIndex / 12);
}

function noteCoveringCell(step, semitone) {
  return state.clip.notes.find((note) => (
    note.semitone === semitone
    && note.step <= step
    && step < note.step + note.durationSteps
  ));
}

function toggleNote(step, lane) {
  const existing = noteCoveringCell(step, lane.semitone);
  let createdNote = null;

  if (existing) {
    state.clip.notes = state.clip.notes.filter((note) => note.id !== existing.id);
  } else {
    createdNote = {
      id: makeId("note"),
      step,
      semitone: lane.semitone,
      degree: lane.degree,
      durationSteps: 1,
    };
    state.clip.notes.push(createdNote);
    state.clip.notes.sort((a, b) => a.step - b.step || a.semitone - b.semitone);
  }

  touchClip();
  renderGrid();
  renderSummary();

  if (createdNote) {
    previewNote(createdNote);
  }
}

function setNoteAt(step, semitone, degree, durationSteps = 1) {
  const totalSteps = totalClipSteps();
  const duration = Math.trunc(clampNumber(durationSteps, 1, totalSteps - step, 1));
  const noteEnd = step + duration;
  const note = {
    id: makeId("note"),
    step,
    semitone,
    degree,
    durationSteps: duration,
  };

  state.clip.notes = state.clip.notes.filter((candidate) => (
    candidate.semitone !== semitone
    || candidate.step >= noteEnd
    || candidate.step + candidate.durationSteps <= step
  ));
  state.clip.notes.push(note);
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.quality, state.clip.stepsPerBar);
  touchClip();
  renderGrid();
  renderSummary();
  previewNote(note);
  return note;
}

function render() {
  renderTitleWithOrigin();
  els.bpmInput.value = state.clip.bpm;
  els.soundBankSelect.value = normalizeSoundfontId(state.clip.soundBank);
  els.keySelect.value = state.clip.key;
  els.qualitySelect.value = state.clip.quality;
  renderLaneModeDropdown();
  renderGrid();
  renderGridSettings();
  renderSummary();
  renderFileMenuState();
  renderAdminControls();
  renderReturnControls();
  renderKeyboardBindings();
}

function renderSummary() {
  els.doubleButton.disabled = state.clip.bars >= MAX_BARS;
  els.undoubleButton.classList.toggle("d-none", state.clip.bars <= 1);
  renderStepRecordButton();
  renderGridFooter();
  renderPlayButton();
}

function renderLaneModeDropdown() {
  const showChromatic = Boolean(state.clip.showChromatic);
  els.laneModeDropdownButton.textContent = showChromatic ? "Chromatic" : "Scale";
  els.scaleModeButton.classList.toggle("active", !showChromatic);
  els.scaleModeButton.setAttribute("aria-current", showChromatic ? "false" : "true");
  els.chromaticModeButton.classList.toggle("active", showChromatic);
  els.chromaticModeButton.setAttribute("aria-current", showChromatic ? "true" : "false");
}

function renderPlayButton() {
  els.playButton.innerHTML = "<span class=\"play-icon\" aria-hidden=\"true\"></span>";
  els.playButton.setAttribute("aria-label", state.playing ? "Stop" : "Play");
  els.playButton.setAttribute("title", state.playing ? "Stop" : "Play");
}

function renderStepRecordButton() {
  els.stepRecordButton.classList.toggle("active", state.stepRecord.active);
  els.stepRecordButton.setAttribute("aria-pressed", state.stepRecord.active ? "true" : "false");
  els.stepRecordButton.setAttribute("aria-label", state.stepRecord.active ? "Stop Step Record" : "Step Record");
  els.stepRecordButton.setAttribute("title", state.stepRecord.active ? "Stop Step Record" : "Step Record");
}

function renderGridFooter() {
  els.clipGridFooter.style.setProperty("--step-count", totalClipSteps());
  els.clipGridFooter.replaceChildren();
  if (state.stepRecord.active) {
    const spacer = document.createElement("span");
    spacer.className = "clip-grid-footer-spacer";
    spacer.setAttribute("aria-hidden", "true");
    const status = document.createElement("span");
    status.className = "clip-grid-footer-status";
    status.textContent = `Step Record: ${noteLengthLabel(state.stepRecord.noteLengthSteps)} | Octave ${state.stepRecord.octave + 1}`;
    els.clipGridFooter.append(spacer, status);
  }
  els.clipGridFooter.classList.toggle("is-active", state.stepRecord.active);
}

function renderKeyboardBindings() {
  const scaleSemitones = stepRecordScaleSemitones();
  const bindingRows = state.clip.showChromatic ? STEP_RECORD_BINDING_ROWS.chromatic : STEP_RECORD_BINDING_ROWS.scale;
  const octaveOffset = state.stepRecord.octave * 12;
  const rows = bindingRows.map((layoutRow, rowIndex) => {
    const row = document.createElement("div");
    row.className = `keyboard-bindings-row keyboard-bindings-row-${rowIndex + 1}`;

    layoutRow.forEach((item) => {
      if (!item) {
        const spacer = document.createElement("span");
        spacer.className = "binding-chip-spacer";
        row.append(spacer);
        return;
      }

      const [, actionKey, value] = item;
      const isNoteBinding = typeof value === "number";
      const semitone = isNoteBinding && state.clip.showChromatic && actionKey.length === 1 && /[A-Z]/.test(actionKey)
        ? value
        : scaleSemitones[value];
      const chip = document.createElement("span");
      chip.className = `binding-chip${isNoteBinding ? "" : " binding-chip-action"}`;
      chip.innerHTML = "<kbd></kbd><span></span>";
      chip.querySelector("kbd").textContent = actionKey;
      chip.querySelector("span").textContent = isNoteBinding ? noteLabelForSemitone(octaveOffset + semitone) : value;
      row.append(chip);
    });

    return row;
  });

  els.keyboardBindings.replaceChildren(...rows);
}

function renderReturnControls() {
  els.backToProgressionButton.classList.toggle("d-none", !state.returnHandoff);
}

function renderGrid() {
  const lanes = lanesForClip();
  const totalSteps = totalClipSteps();
  els.clipGrid.style.setProperty("--step-count", totalSteps);

  const fragment = document.createDocumentFragment();
  const corner = document.createElement("div");
  corner.className = "grid-corner";
  fragment.append(corner);

  for (let step = 0; step < totalSteps; step += 1) {
    const header = document.createElement("div");
    header.className = [
      "step-header",
      step % state.clip.stepsPerBar === 0 ? "is-bar-start" : "",
      state.stepRecord.active && step === state.stepRecord.step ? "is-step-record-column" : "",
    ].filter(Boolean).join(" ");
    header.textContent = step % state.clip.stepsPerBar === 0 ? `${Math.floor(step / state.clip.stepsPerBar) + 1}` : `${(step % state.clip.stepsPerBar) + 1}`;
    fragment.append(header);
  }

  for (const lane of lanes) {
    const label = document.createElement("div");
    const octaveBoundaryClass = isOctaveBoundaryAbove(lane.semitone) ? " is-octave-boundary" : "";
    const stepRecordOctaveClass = state.stepRecord.active && octaveForSemitone(lane.semitone) === state.stepRecord.octave
      ? " is-step-record-octave"
      : "";
    label.className = `lane-label${lane.scaleTone ? "" : " is-chromatic"}${octaveBoundaryClass}${stepRecordOctaveClass}`;
    label.style.setProperty("--note-color", lane.color);
    label.textContent = lane.label;
    label.title = `${lane.label} (${chromaticNames[lane.semitone % 12]})`;
    fragment.append(label);

    for (let step = 0; step < totalSteps; step += 1) {
      const note = noteCoveringCell(step, lane.semitone);
      const isStart = note?.step === step;
      const isEnd = note && note.step + note.durationSteps - 1 === step;
      const cell = document.createElement("button");
      cell.className = [
        "note-cell",
        note ? "is-active" : "",
        isStart ? "is-note-start" : "",
        isEnd ? "is-note-end" : "",
        step % state.clip.stepsPerBar === 0 ? "is-bar-start" : "",
        state.stepRecord.active && step === state.stepRecord.step ? "is-step-record-column" : "",
        octaveBoundaryClass ? "is-octave-boundary" : "",
      ].filter(Boolean).join(" ");
      cell.type = "button";
      cell.dataset.step = `${step}`;
      cell.dataset.semitone = `${lane.semitone}`;
      cell.style.setProperty("--note-color", lane.color);
      cell.setAttribute("aria-label", `${note ? "Remove" : "Add"} ${lane.label} at step ${step + 1}`);
      cell.addEventListener("click", () => toggleNote(step, lane));

      if (isStart) {
        cell.append(createResizeHandle(note, "left"));
      }
      if (isEnd) {
        cell.append(createResizeHandle(note, "right"));
      }

      fragment.append(cell);
    }
  }

  const playhead = document.createElement("div");
  playhead.className = `clip-playhead${state.playing ? " is-visible" : ""}`;
  playhead.setAttribute("aria-hidden", "true");
  fragment.append(playhead);

  els.clipGrid.replaceChildren(fragment);
  updatePlayheadPosition();
  renderGridFooter();
}

function renderGridSettings() {
  els.gridOctaves1Radio.checked = state.clip.gridOctaves === 1;
  els.gridOctaves2Radio.checked = state.clip.gridOctaves === 2;
  els.gridOctaves3Radio.checked = state.clip.gridOctaves === 3;
  els.gridResolution4Radio.checked = state.clip.stepsPerBar === 4;
  els.gridResolution8Radio.checked = state.clip.stepsPerBar === 8;
  els.gridResolution16Radio.checked = state.clip.stepsPerBar === 16;
  els.baseOctaveOutput.value = `${state.clip.baseOctave}`;
  els.octaveDownButton.disabled = state.clip.baseOctave <= MIN_BASE_OCTAVE;
  els.octaveUpButton.disabled = state.clip.baseOctave >= MAX_BASE_OCTAVE;
}

function createResizeHandle(note, edge) {
  const handle = document.createElement("span");
  handle.className = `note-resize-handle is-${edge}`;
  handle.role = "separator";
  handle.setAttribute("aria-label", `${edge === "left" ? "Start" : "End"} of note`);
  handle.addEventListener("click", (event) => event.stopPropagation());
  handle.addEventListener("pointerdown", (event) => startNoteResize(event, note, edge));
  return handle;
}

function totalClipSteps() {
  return state.clip.bars * state.clip.stepsPerBar;
}

function stepFromClientX(clientX) {
  const gridRect = els.clipGrid.getBoundingClientRect();
  const labelWidth = els.clipGrid.querySelector(".grid-corner")?.getBoundingClientRect().width || 0;
  const stepWidth = els.clipGrid.querySelector(".step-header")?.getBoundingClientRect().width || 1;
  const rawStep = Math.floor((clientX - gridRect.left - labelWidth) / stepWidth);

  return Math.trunc(clampNumber(rawStep, 0, totalClipSteps() - 1, 0));
}

function startNoteResize(event, note, edge) {
  event.preventDefault();
  event.stopPropagation();
  state.resizingNote = {
    noteId: note.id,
    edge,
    moved: false,
  };
  window.addEventListener("pointermove", resizeNoteFromPointer);
  window.addEventListener("pointerup", finishNoteResize, { once: true });
}

function resizeNoteFromPointer(event) {
  if (!state.resizingNote) {
    return;
  }

  const note = state.clip.notes.find((candidate) => candidate.id === state.resizingNote.noteId);
  if (!note) {
    finishNoteResize();
    return;
  }

  const step = stepFromClientX(event.clientX);
  const originalStep = note.step;
  const originalDuration = note.durationSteps;
  resizeNoteToStep(note, state.resizingNote.edge, step);

  if (note.step !== originalStep || note.durationSteps !== originalDuration) {
    state.resizingNote.moved = true;
    renderGrid();
  }
}

function finishNoteResize() {
  if (!state.resizingNote) {
    return;
  }

  const changed = state.resizingNote.moved;
  state.resizingNote = null;
  window.removeEventListener("pointermove", resizeNoteFromPointer);

  if (changed) {
    state.clip.notes.sort((a, b) => a.step - b.step || a.semitone - b.semitone);
    touchClip();
    renderGrid();
  }
}

function resizeNoteToStep(note, edge, step) {
  const laneNotes = state.clip.notes
    .filter((candidate) => candidate.semitone === note.semitone && candidate.id !== note.id)
    .sort((a, b) => a.step - b.step);
  const noteEnd = note.step + note.durationSteps;

  if (edge === "left") {
    const previousNote = laneNotes.filter((candidate) => candidate.step < note.step).at(-1);
    const minStart = previousNote ? previousNote.step + previousNote.durationSteps : 0;
    const maxStart = noteEnd - 1;
    const nextStart = Math.trunc(clampNumber(step, minStart, maxStart, note.step));
    note.durationSteps = noteEnd - nextStart;
    note.step = nextStart;
    return;
  }

  const nextNote = laneNotes.find((candidate) => candidate.step > note.step);
  const maxEnd = nextNote ? nextNote.step : totalClipSteps();
  const minEnd = note.step + 1;
  const nextEnd = Math.trunc(clampNumber(step + 1, minEnd, maxEnd, noteEnd));
  note.durationSteps = nextEnd - note.step;
}

function setTitleFromInput() {
  state.clip.title = state.clip.title.trim().slice(0, 160) || DEFAULT_TITLE;
  touchClip();
}

function setBpmFromInput() {
  state.clip.bpm = clampNumber(els.bpmInput.value, MIN_BPM, MAX_BPM, 120);
  els.bpmInput.value = state.clip.bpm;
  touchClip();
}

function setSoundBankFromInput() {
  state.clip.soundBank = normalizeSoundfontId(els.soundBankSelect.value);
  playSoundfontPreview("fatboy", state.clip.soundBank);
  touchClip();
}

function setKeyFromInput() {
  state.clip.key = keys.includes(els.keySelect.value) ? els.keySelect.value : "C";
  touchClip();
  renderGrid();
  renderKeyboardBindings();
}

function setQualityFromInput() {
  state.clip.quality = els.qualitySelect.value === "minor" ? "minor" : "major";
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.quality);
  touchClip();
  renderGrid();
  renderKeyboardBindings();
}

function setChromaticMode(showChromatic) {
  state.clip.showChromatic = showChromatic;
  touchClip();
  renderLaneModeDropdown();
  renderFileMenuState();
  renderGrid();
  renderKeyboardBindings();
}

function showClipSettingsModal() {
  renderGridSettings();
  window.bootstrap.Modal.getOrCreateInstance(els.clipSettingsModal).show();
}

function setGridOctavesFromInput(event) {
  state.clip.gridOctaves = Math.trunc(clampNumber(event.target.value, 1, MAX_GRID_OCTAVES, 1));
  state.stepRecord.octave = Math.min(state.stepRecord.octave, state.clip.gridOctaves - 1);
  touchClip();
  renderGridSettings();
  renderGrid();
  renderKeyboardBindings();
}

function setGridResolutionFromInput(event) {
  const nextStepsPerBar = validStepsPerBar(event.target.value, state.clip.stepsPerBar);
  const previousStepsPerBar = state.clip.stepsPerBar;

  if (nextStepsPerBar === previousStepsPerBar) {
    return;
  }

  state.clip.stepsPerBar = nextStepsPerBar;
  state.clip.notes = state.clip.notes.map((note) => ({
    ...note,
    step: Math.round((note.step / previousStepsPerBar) * nextStepsPerBar),
    durationSteps: Math.max(1, Math.round((note.durationSteps / previousStepsPerBar) * nextStepsPerBar)),
  }));
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.quality, state.clip.stepsPerBar);
  state.stepRecord.octave = Math.min(state.stepRecord.octave, state.clip.gridOctaves - 1);
  state.stepRecord.noteLengthSteps = Math.min(state.stepRecord.noteLengthSteps, maxStepRecordNoteLength());
  touchClip();
  renderGridSettings();
  renderGrid();
}

function shiftBaseOctave(direction) {
  state.clip.baseOctave = Math.trunc(clampNumber(state.clip.baseOctave + direction, MIN_BASE_OCTAVE, MAX_BASE_OCTAVE, DEFAULT_BASE_OCTAVE));
  touchClip();
  renderGridSettings();
  renderGrid();
  renderKeyboardBindings();
}

function octaveForSemitone(semitone) {
  return Math.trunc(clampNumber(Math.floor(semitone / 12), 0, state.clip.gridOctaves - 1, 0));
}

function doubleClip() {
  if (state.clip.bars >= MAX_BARS) {
    return;
  }

  const oldTotalSteps = totalClipSteps();
  const oldNotes = state.clip.notes.map((note) => ({ ...note }));
  state.clip.bars *= 2;
  state.clip.notes.push(...oldNotes.map((note) => ({
    ...note,
    id: makeId("note"),
    step: note.step + oldTotalSteps,
  })));
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.quality, state.clip.stepsPerBar);
  touchClip();
  renderGrid();
  renderSummary();
}

function undoubleClip() {
  if (state.clip.bars <= 1) {
    return;
  }

  state.clip.bars /= 2;
  state.clip.notes = normalizeNotes(state.clip.notes, state.clip.bars, state.clip.quality, state.clip.stepsPerBar);
  state.stepRecord.step = Math.min(state.stepRecord.step, totalClipSteps() - 1);
  state.stepRecord.history = state.stepRecord.history.filter((entry) => entry.step < totalClipSteps());
  touchClip();
  renderGrid();
  renderSummary();
}

function returnToProgression() {
  if (!state.returnHandoff) {
    return;
  }

  setTitleFromInput();
  saveState();
  try {
    window.localStorage.setItem(CLIP_EDITOR_RETURN_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      ...state.returnHandoff,
      clip: state.clip,
    }));
    window.localStorage.removeItem(CLIP_EDITOR_HANDOFF_KEY);
  } catch {
    window.alert("Could not return this clip because browser storage is unavailable.");
    return;
  }

  const returnUrl = state.returnHandoff.returnUrl || "/songs";
  state.returnHandoff = null;
  saveState();
  window.location.href = returnUrl;
}

function newClip() {
  if (!confirmDiscardUnsavedClip("Start a new clip")) {
    return;
  }

  stopPlayback();
  state.clip = createDefaultClip();
  state.serverClipId = null;
  state.serverClipSignature = null;
  state.serverClipLocked = false;
  state.loadedOriginBardName = "";
  state.loadedClipStats = null;
  state.returnHandoff = null;
  scheduleStateSave();
  render();
}

function shareLinkForCurrentClip() {
  if (!state.serverClipId) {
    return "";
  }

  const url = new window.URL(window.location.href);
  url.pathname = "/clips/";
  url.search = "";
  url.hash = "";
  url.searchParams.set("loadClipId", state.serverClipId);
  return url.toString();
}

function showShareModal() {
  const link = shareLinkForCurrentClip();
  const isDirty = hasUnsavedClipChanges();
  els.shareLinkInput.value = link;
  els.copyShareLinkButton.disabled = !link;
  els.shareLinkMessage.textContent = "";
  els.shareLinkHelp.textContent = link
    ? `This deep link opens the saved clip. ${isDirty ? "Save again before sharing if you want this link to include your latest edits. " : ""}For social media, mark the clip shared when you save so other people can open it.`
    : "Save this clip first to create a deep link. For social media, mark it shared when you save so other people can open it.";
  window.bootstrap.Modal.getOrCreateInstance(els.shareModal).show();
}

async function copyShareLink() {
  const link = els.shareLinkInput.value;
  if (!link) {
    els.shareLinkMessage.textContent = "Save this clip first to create a link.";
    return;
  }

  try {
    await navigator.clipboard.writeText(link);
  } catch {
    els.shareLinkInput.select();
    document.execCommand("copy");
  }
  els.shareLinkMessage.textContent = "Link copied.";
}

async function loadLeaderboardClipFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const clipId = params.get("loadClipId");
  if (!clipId) {
    return;
  }

  try {
    const body = await apiRequest(`/api/leaderboard/clips/${encodeURIComponent(clipId)}/use`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const loadedClip = body.clip?.clip;
    if (!loadedClip) {
      throw new Error("The server did not return clip JSON.");
    }

    stopPlayback();
    state.clip = normalizeClip(loadedClip);
    state.serverClipId = body.clip.id ? String(body.clip.id) : null;
    state.serverClipLocked = Boolean(body.clip.locked || !body.clip.owned);
    state.serverClipSignature = state.serverClipId ? clipServerSignature() : null;
    state.loadedClipStats = normalizeLoadedClipStats(body.clip);
    state.loadedOriginBardName = state.loadedClipStats?.bardName || "";
    state.returnHandoff = null;
    scheduleStateSave();
    render();
    window.history.replaceState({}, "", window.location.pathname);
  } catch (error) {
    window.alert(error.message || "Could not load that clip.");
  }
}

async function ensureSynth() {
  await Tone.start();

  await ensureSamplerForSoundfont(state.clip.soundBank);
}

async function ensureSamplerForSoundfont(id) {
  const soundfontId = normalizeSoundfontId(id);
  const existing = state.samplers.get(soundfontId);
  if (existing?.ready) {
    return existing;
  }
  if (existing?.loadPromise) {
    await existing.loadPromise;
    return existing;
  }

  const config = soundfontConfig(soundfontId);
  await ensureSoundfontData(config);
  const soundfont = window.MIDI?.Soundfont?.[config.globalName];
  if (!soundfont) {
    throw new Error(`Sound bank is not loaded: ${config.label}`);
  }

  const output = new Tone.Gain(config.gain).toDestination();
  const samplerState = {
    config,
    output,
    sampler: null,
    ready: false,
    loadPromise: null,
  };

  samplerState.sampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: config.release,
    onload() {
      samplerState.ready = true;
    },
  }).connect(output);
  samplerState.loadPromise = Tone.loaded().then(() => {
    samplerState.ready = true;
    return samplerState;
  });
  state.samplers.set(config.id, samplerState);
  await samplerState.loadPromise;
  return samplerState;
}

function midiForNote(note) {
  const rootMidi = Note.midi(`${state.clip.key}${state.clip.baseOctave}`) || 60;
  return rootMidi + note.semitone;
}

function secondsPerStep() {
  return (60 / state.clip.bpm * 4) / state.clip.stepsPerBar;
}

function ticksPerClipStep(clip = state.clip) {
  return Math.round((PPQ * 4) / clip.stepsPerBar);
}

function showMidiExportDialog() {
  if (!shouldShowMidiExportNotice()) {
    exportMidi();
    return;
  }

  window.bootstrap.Modal.getOrCreateInstance(els.midiExportModal).show();
}

function confirmMidiExport(options = {}) {
  if (options.remember) {
    rememberMidiExportNoticeDismissed();
  }
  window.bootstrap.Modal.getOrCreateInstance(els.midiExportModal).hide();
  exportMidi();
}

function exportMidi() {
  const clip = state.clip;
  const midi = createMidiDocument({
    name: clip.title,
    ppq: PPQ,
    bpm: clip.bpm,
    timeSignature: [4, 4],
    keySignature: { key: clip.key, scale: clip.quality === "minor" ? "minor" : "major" },
  });
  const track = addMidiTrack(midi, {
    name: clip.title || DEFAULT_TITLE,
  });
  const rootMidi = Note.midi(`${clip.key}${clip.baseOctave}`) || 60;
  const stepTicks = ticksPerClipStep(clip);

  clip.notes.forEach((note) => {
    addMidiNote(track, {
      midi: rootMidi + note.semitone,
      ticks: note.step * stepTicks,
      durationTicks: Math.max(1, note.durationSteps * stepTicks),
      velocity: 0.72,
    });
  });

  downloadMidi(midi, clip.title || DEFAULT_TITLE);
}

async function previewNote(note) {
  try {
    await ensureSynth();
  } catch (error) {
    window.alert(error.message || "Could not load that sound bank.");
    return;
  }

  const samplerState = state.samplers.get(normalizeSoundfontId(state.clip.soundBank));
  const midi = midiForNote(note);
  const noteName = Tone.Frequency(midi, "midi").toNote();
  const duration = Math.max(0.08, note.durationSteps * secondsPerStep() * 0.92);

  samplerState?.sampler?.triggerAttackRelease(
    noteName,
    duration,
    Tone.now(),
    0.72 * (samplerState?.config.defaultVelocity || 0.7),
  );
}

async function playClip() {
  if (state.stepRecord.active) {
    stopStepRecord();
  }

  if (state.playing) {
    stopPlayback();
    return;
  }

  try {
    await ensureSynth();
  } catch (error) {
    window.alert(error.message || "Could not load that sound bank.");
    return;
  }

  state.playing = true;
  state.playbackStartedAt = Tone.now() + 0.05;
  els.playButton.classList.add("active");
  renderPlayButton();

  scheduleClipCycle(state.playbackStartedAt);
  startPlayheadAnimation();
}

function scheduleClipCycle(startedAt) {
  if (!state.playing) {
    return;
  }

  const stepSeconds = secondsPerStep();
  const totalSeconds = totalClipSteps() * stepSeconds;

  const samplerState = state.samplers.get(normalizeSoundfontId(state.clip.soundBank));

  for (const note of state.clip.notes) {
    const midi = midiForNote(note);
    const noteName = Tone.Frequency(midi, "midi").toNote();
    const duration = Math.max(0.08, note.durationSteps * stepSeconds * 0.92);
    addScheduledTimeout(() => {
      samplerState?.sampler?.triggerAttackRelease(
        noteName,
        duration,
        Tone.now(),
        0.72 * (samplerState?.config.defaultVelocity || 0.7),
      );
    }, Math.max(0, (startedAt + note.step * stepSeconds - Tone.now()) * 1000));
  }

  const nextCycleAt = startedAt + totalSeconds;
  addScheduledTimeout(() => {
    scheduleClipCycle(nextCycleAt);
  }, Math.max(80, (nextCycleAt - Tone.now()) * 1000));
}

function addScheduledTimeout(callback, delay) {
  const timeoutId = window.setTimeout(() => {
    state.scheduledIds = state.scheduledIds.filter((id) => id !== timeoutId);
    callback();
  }, delay);
  state.scheduledIds.push(timeoutId);
  return timeoutId;
}

function stopPlayback() {
  for (const id of state.scheduledIds) {
    window.clearTimeout(id);
  }
  state.scheduledIds = [];
  state.playing = false;
  state.playbackStartedAt = 0;
  stopPlayheadAnimation();
  els.playButton.classList.remove("active");
  renderPlayButton();
  updatePlayheadPosition();
  state.samplers.forEach((samplerState) => samplerState.sampler?.releaseAll?.());
}

function startStepRecord() {
  stopPlayback();
  state.stepRecord.active = true;
  state.stepRecord.step = 0;
  state.stepRecord.octave = Math.trunc(clampNumber(state.stepRecord.octave, 0, state.clip.gridOctaves - 1, 0));
  state.stepRecord.noteLengthSteps = Math.trunc(clampNumber(state.stepRecord.noteLengthSteps, 1, maxStepRecordNoteLength(), 1));
  state.stepRecord.history = [];
  renderGrid();
  renderSummary();
  renderKeyboardBindings();
  els.clipGrid.focus({ preventScroll: true });
  scrollStepRecordColumnIntoView();
}

function stopStepRecord() {
  if (state.stepRecord.debounceTimer) {
    window.clearTimeout(state.stepRecord.debounceTimer);
    state.stepRecord.debounceTimer = null;
    state.stepRecord.pendingSemitones.clear();
  }
  state.stepRecord.active = false;
  renderGrid();
  renderSummary();
}

function toggleStepRecord() {
  if (state.stepRecord.active) {
    stopStepRecord();
  } else {
    startStepRecord();
  }
}

function moveStepRecordColumn(direction) {
  state.stepRecord.step = wrappedStep(state.stepRecord.step + direction);
  renderGrid();
  scrollStepRecordColumnIntoView();
}

function wrappedStep(step) {
  const totalSteps = totalClipSteps();
  return ((step % totalSteps) + totalSteps) % totalSteps;
}

function moveStepRecordOctave(direction) {
  state.stepRecord.octave = Math.trunc(clampNumber(state.stepRecord.octave + direction, 0, state.clip.gridOctaves - 1, state.stepRecord.octave));
  renderGrid();
  renderGridFooter();
  renderKeyboardBindings();
}

function queueStepRecordSemitone(semitone) {
  const relativeSemitone = Math.trunc(Number(semitone));
  if (!Number.isInteger(relativeSemitone) || relativeSemitone < 0 || relativeSemitone >= state.clip.gridOctaves * 12) {
    return;
  }

  state.stepRecord.pendingSemitones.add(relativeSemitone);
  if (state.stepRecord.debounceTimer) {
    window.clearTimeout(state.stepRecord.debounceTimer);
  }
  state.stepRecord.debounceTimer = window.setTimeout(flushStepRecordNotes, STEP_RECORD_DEBOUNCE_MS);
}

function flushStepRecordNotes() {
  const semitones = [...state.stepRecord.pendingSemitones].sort((a, b) => a - b);
  state.stepRecord.pendingSemitones.clear();
  state.stepRecord.debounceTimer = null;

  if (!state.stepRecord.active || !semitones.length) {
    return;
  }

  const step = state.stepRecord.step;
  const scaleSemitones = semitonesForQuality();
  const createdNoteIds = [];
  for (const relativeSemitone of semitones) {
    const absoluteSemitone = (state.stepRecord.octave * 12) + relativeSemitone;
    if (absoluteSemitone >= state.clip.gridOctaves * 12) {
      continue;
    }

    const note = setNoteAt(
      step,
      absoluteSemitone,
      scaleSemitones.indexOf(relativeSemitone % 12),
      state.stepRecord.noteLengthSteps,
    );
    if (note) {
      createdNoteIds.push(note.id);
    }
  }
  if (createdNoteIds.length) {
    state.stepRecord.history.push({ step, noteIds: createdNoteIds });
    moveStepRecordColumn(1);
  }
}

function deletePreviousStepRecordEntry() {
  if (state.stepRecord.debounceTimer) {
    window.clearTimeout(state.stepRecord.debounceTimer);
    flushStepRecordNotes();
  }

  const entry = state.stepRecord.history.pop();
  if (!entry) {
    moveStepRecordColumn(-1);
    return;
  }

  const noteIds = new Set(entry.noteIds);
  state.clip.notes = state.clip.notes.filter((note) => !noteIds.has(note.id));
  state.stepRecord.step = entry.step;
  touchClip();
  renderGrid();
  renderSummary();
  scrollStepRecordColumnIntoView();
}

function adjustStepRecordNoteLength(direction) {
  const nextLength = state.stepRecord.noteLengthSteps + direction;
  state.stepRecord.noteLengthSteps = Math.trunc(clampNumber(nextLength, 1, maxStepRecordNoteLength(), state.stepRecord.noteLengthSteps));
  renderGridFooter();
}

function maxStepRecordNoteLength() {
  return Math.max(1, state.clip.stepsPerBar / 4);
}

function noteLengthLabel(durationSteps) {
  const denominator = state.clip.stepsPerBar / durationSteps;
  return Number.isInteger(denominator) ? `1/${denominator}` : `${durationSteps} steps`;
}

function scrollStepRecordColumnIntoView() {
  const header = els.clipGrid.querySelector(`.step-header:nth-child(${state.stepRecord.step + 2})`);
  header?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
}

function startPlayheadAnimation() {
  stopPlayheadAnimation();

  const animate = () => {
    updatePlayheadPosition();
    if (state.playing) {
      state.playheadAnimationFrame = window.requestAnimationFrame(animate);
    }
  };

  state.playheadAnimationFrame = window.requestAnimationFrame(animate);
}

function stopPlayheadAnimation() {
  if (!state.playheadAnimationFrame) {
    return;
  }

  window.cancelAnimationFrame(state.playheadAnimationFrame);
  state.playheadAnimationFrame = null;
}

function updatePlayheadPosition() {
  const playhead = els.clipGrid.querySelector(".clip-playhead");
  if (!playhead) {
    return;
  }

  playhead.classList.toggle("is-visible", state.playing);

  if (!state.playing || !state.playbackStartedAt) {
    els.clipGrid.style.setProperty("--playhead-left", "var(--lane-label-width)");
    return;
  }

  const stepSeconds = secondsPerStep();
  const totalSeconds = totalClipSteps() * stepSeconds;
  const elapsedSeconds = Math.max(0, Tone.now() - state.playbackStartedAt);
  const cycleSeconds = totalSeconds > 0 ? elapsedSeconds % totalSeconds : 0;
  const stepPosition = cycleSeconds / stepSeconds;

  els.clipGrid.style.setProperty(
    "--playhead-left",
    `calc(var(--lane-label-width) + (${stepPosition} * var(--step-width)))`,
  );
}

function handleKeydown(event) {
  const modifierKey = event.ctrlKey || event.metaKey;

  if (modifierKey && event.code === "KeyS") {
    event.preventDefault();
    if (event.repeat) {
      return;
    }
    if (event.shiftKey) {
      showSaveClipModal();
    } else {
      saveClip();
    }
    return;
  }

  if (state.stepRecord.active && handleStepRecordKeydown(event)) {
    return;
  }

  if (handleStepRecordPreviewKeydown(event)) {
    return;
  }

  if (modifierKey && event.code === "KeyN") {
    event.preventDefault();
    if (!event.repeat) {
      newClip();
    }
    return;
  }

  if (event.code !== "Space" || event.repeat || shouldIgnoreTransportKeydown(event.target)) {
    return;
  }

  event.preventDefault();
  playClip();
}

function stepRecordSemitoneForKeydown(event) {
  const scaleKey = STEP_RECORD_SCALE_KEYS.find(([code, key]) => code === event.code || key === event.key);
  if (scaleKey) {
    return stepRecordScaleSemitones()[scaleKey[2]];
  }

  const letterKey = state.clip.showChromatic
    ? STEP_RECORD_CHROMATIC_KEYS.find(([code]) => code === event.code)
    : STEP_RECORD_WHITE_KEYS.find(([code]) => code === event.code);
  if (!letterKey) {
    return null;
  }

  return state.clip.showChromatic
    ? letterKey[2]
    : stepRecordScaleSemitones()[letterKey[2]];
}

function handleStepRecordPreviewKeydown(event) {
  if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || shouldIgnoreTransportKeydown(event.target)) {
    return false;
  }

  const semitone = stepRecordSemitoneForKeydown(event);
  if (semitone === null) {
    return false;
  }

  const absoluteSemitone = (state.stepRecord.octave * 12) + semitone;
  if (absoluteSemitone >= state.clip.gridOctaves * 12) {
    return false;
  }

  event.preventDefault();
  previewNote({
    semitone: absoluteSemitone,
    durationSteps: state.stepRecord.noteLengthSteps,
  });
  return true;
}

function handleStepRecordKeydown(event) {
  if (shouldIgnoreTransportKeydown(event.target)) {
    return false;
  }

  if (event.code === "Escape") {
    event.preventDefault();
    stopStepRecord();
    return true;
  }
  if (event.code === "ArrowLeft" || event.code === "ArrowRight") {
    event.preventDefault();
    if (!event.repeat) {
      moveStepRecordColumn(event.code === "ArrowLeft" ? -1 : 1);
    }
    return true;
  }
  if (event.code === "ArrowUp" || event.code === "ArrowDown") {
    event.preventDefault();
    if (!event.repeat) {
      moveStepRecordOctave(event.code === "ArrowUp" ? 1 : -1);
    }
    return true;
  }
  if (event.code === "Backspace") {
    event.preventDefault();
    if (!event.repeat) {
      deletePreviousStepRecordEntry();
    }
    return true;
  }
  if (event.key === "+" || event.key === "=") {
    event.preventDefault();
    if (!event.repeat) {
      adjustStepRecordNoteLength(1);
    }
    return true;
  }
  if (event.key === "-" || event.key === "_") {
    event.preventDefault();
    if (!event.repeat) {
      adjustStepRecordNoteLength(-1);
    }
    return true;
  }

  const semitone = stepRecordSemitoneForKeydown(event);
  if (semitone !== null) {
    event.preventDefault();
    if (!event.repeat) {
      queueStepRecordSemitone(semitone);
    }
    return true;
  }

  return false;
}

function shouldIgnoreTransportKeydown(target) {
  if (!(target instanceof window.Element)) {
    return false;
  }

  return Boolean(target.closest("input, select, textarea, button, [contenteditable='true']"));
}

function populateKeySelect() {
  els.keySelect.replaceChildren(...keys.map((key) => {
    const option = document.createElement("option");
    option.value = key;
    option.textContent = key;
    return option;
  }));
}

function populateSoundBankSelect() {
  els.soundBankSelect.replaceChildren(...FATBOY_SOUNDFONTS.map((soundfont) => {
    const option = document.createElement("option");
    option.value = soundfont.id;
    option.textContent = soundfont.label;
    return option;
  }));
}

function bindEvents() {
  els.fileNewButton.addEventListener("click", newClip);
  els.fileLoadButton.addEventListener("click", showLoadClipModal);
  els.fileSaveButton.addEventListener("click", saveClip);
  els.fileSaveAsButton.addEventListener("click", showSaveClipModal);
  els.fileShareButton.addEventListener("click", showShareModal);
  els.copyShareLinkButton.addEventListener("click", copyShareLink);
  els.fileExportMidiButton.addEventListener("click", showMidiExportDialog);
  els.confirmMidiExportButton.addEventListener("click", () => confirmMidiExport());
  els.midiExportNeverShowButton.addEventListener("click", () => confirmMidiExport({ remember: true }));
  els.backToProgressionButton.addEventListener("click", returnToProgression);
  els.saveClipNameInput.addEventListener("input", () => {
    clearSaveModalSelectionIfEdited();
    renderSaveModalDuplicateState();
  });
  els.saveClipTypeSelect.addEventListener("change", () => {
    clearSaveModalSelectionIfEdited();
    renderSaveClipList(state.saveModalClips);
    renderSaveModalDuplicateState();
  });
  els.saveSharedCheckbox.addEventListener("change", renderSaveSharedInfo);
  document.querySelectorAll("[data-save-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.saveSort || "updatedAt";
      if (state.saveModalSortKey === key) {
        state.saveModalSortDirection = state.saveModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.saveModalSortKey = key;
        state.saveModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderSaveClipList(state.saveModalClips);
    });
  });
  document.querySelectorAll("[data-load-sort]").forEach((button) => {
    button.addEventListener("click", () => {
      const key = button.dataset.loadSort || "updatedAt";
      if (state.loadModalSortKey === key) {
        state.loadModalSortDirection = state.loadModalSortDirection === "asc" ? "desc" : "asc";
      } else {
        state.loadModalSortKey = key;
        state.loadModalSortDirection = key === "updatedAt" ? "desc" : "asc";
      }
      renderLoadClipList(state.loadModalClips);
    });
  });
  document.querySelectorAll("[data-load-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      state.loadModalMode = button.dataset.loadMode === "shared" ? "shared" : "owned";
      renderLoadClipList(state.loadModalClips);
    });
  });
  els.confirmLoadClipButton?.addEventListener("click", loadSelectedClipFromModal);
  els.loadClipModal.addEventListener("hidden.bs.modal", cancelLoadClipPreview);
  els.confirmSaveClipButton.addEventListener("click", saveClipFromModal);
  els.bpmInput.addEventListener("change", setBpmFromInput);
  els.soundBankSelect.addEventListener("change", setSoundBankFromInput);
  els.keySelect.addEventListener("change", setKeyFromInput);
  els.qualitySelect.addEventListener("change", setQualityFromInput);
  els.scaleModeButton.addEventListener("click", () => setChromaticMode(false));
  els.chromaticModeButton.addEventListener("click", () => setChromaticMode(true));
  els.gridOctaves1Radio.addEventListener("change", setGridOctavesFromInput);
  els.gridOctaves2Radio.addEventListener("change", setGridOctavesFromInput);
  els.gridOctaves3Radio.addEventListener("change", setGridOctavesFromInput);
  els.gridResolution4Radio.addEventListener("change", setGridResolutionFromInput);
  els.gridResolution8Radio.addEventListener("change", setGridResolutionFromInput);
  els.gridResolution16Radio.addEventListener("change", setGridResolutionFromInput);
  els.octaveDownButton.addEventListener("click", () => shiftBaseOctave(-1));
  els.octaveUpButton.addEventListener("click", () => shiftBaseOctave(1));
  els.doubleButton.addEventListener("click", doubleClip);
  els.undoubleButton.addEventListener("click", undoubleClip);
  els.clipSettingsButton.addEventListener("click", showClipSettingsModal);
  els.playButton.addEventListener("click", playClip);
  els.stepRecordButton.addEventListener("click", toggleStepRecord);
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("beforeunload", saveState);
}

populateSoundBankSelect();
populateKeySelect();
loadStoredState();
bindEvents();
render();
loadCurrentUser();
loadLeaderboardClipFromUrl();
